import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { api, type Client } from "./api";

const STORAGE_KEY = "sibit.selectedClientId";

interface ClientContextValue {
  clients: Client[];               // active only
  loaded: boolean;
  selectedClientId: string;
  setSelectedClientId: (id: string) => void;
  refresh: () => Promise<void>;    // call after create/archive so dropdowns update
}

const Ctx = createContext<ClientContextValue | null>(null);

export function ClientProvider({ children }: { children: ReactNode }) {
  const [clients, setClients] = useState<Client[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [selectedClientId, setSelectedState] = useState<string>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) ?? "";
    } catch {
      return "";
    }
  });

  const setSelectedClientId = (id: string) => {
    setSelectedState(id);
    try {
      localStorage.setItem(STORAGE_KEY, id);
    } catch {
      // ignore storage failures
    }
  };

  const refresh = async () => {
    const cs = await api.clients.list();
    setClients(cs);
    setLoaded(true);
    if (cs.length === 0) return;
    // If current selection is gone (archived/deleted) or never set, pick first.
    if (!selectedClientId || !cs.some((c) => c.id === selectedClientId)) {
      setSelectedClientId(cs[0]!.id);
    }
  };

  useEffect(() => {
    refresh().catch(() => setLoaded(true));
  }, []);

  return (
    <Ctx.Provider
      value={{ clients, loaded, selectedClientId, setSelectedClientId, refresh }}
    >
      {children}
    </Ctx.Provider>
  );
}

export function useClientContext(): ClientContextValue {
  const v = useContext(Ctx);
  if (!v) throw new Error("useClientContext outside ClientProvider");
  return v;
}
